"use client";

import type { GuestDataProps } from "@/components/GuestForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type {
  CheckedInGuestSummary,
  ReservationRoomData,
} from "@/types/Reservation";
import {
  BedDouble,
  CheckCircle2,
  Clock,
  Edit2,
  FileCheck2,
  IdCard,
  Plus,
  Trash2,
  User,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GuestDialogForm } from "./GuestDialogForm";

interface RoomCardProps {
  room: ReservationRoomData;
  roomIndex: number;
  draftGuests: GuestDataProps[];
  onAddDraftGuest: (roomIndex: number, guest: GuestDataProps) => void;
  onUpdateDraftGuest: (
    roomIndex: number,
    guestIndex: number,
    guest: GuestDataProps,
  ) => void;
  onRemoveDraftGuest: (roomIndex: number, guestIndex: number) => void;
}

export function RoomCard({
  room,
  roomIndex,
  draftGuests,
  onAddDraftGuest,
  onUpdateDraftGuest,
  onRemoveDraftGuest,
}: RoomCardProps) {
  const { t } = useTranslation("checkIn");
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [editingGuestIndex, setEditingGuestIndex] = useState<number | null>(
    null,
  );

  const checkedInGuests = room.checkedInGuests || [];
  const expectedCount = room.guestsNumber || 1;
  const currentTotal = checkedInGuests.length + draftGuests.length;
  const isRoomFull = currentTotal >= expectedCount;

  const handleOpenAddDialog = () => {
    setEditingGuestIndex(null);
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (index: number) => {
    setEditingGuestIndex(index);
    setIsDialogOpen(true);
  };

  const handleSaveGuest = (guest: GuestDataProps) => {
    const guestWithRoom: GuestDataProps = {
      ...guest,
      idRisPren: room.idRisPren,
    };

    if (editingGuestIndex !== null) {
      onUpdateDraftGuest(roomIndex, editingGuestIndex, guestWithRoom);
    } else {
      onAddDraftGuest(roomIndex, guestWithRoom);
    }
  };

  const guestToEdit: GuestDataProps =
    editingGuestIndex !== null && draftGuests[editingGuestIndex]
      ? draftGuests[editingGuestIndex]
      : {
          idRisPren: room.idRisPren,
          name: "",
          surname: "",
          birthDate: null,
          privacyPolicy: false,
        };

  return (
    <Card className="overflow-hidden border border-border/70 shadow-xs py-0 gap-0">
      <CardHeader className="bg-muted/30 border-b p-4 sm:p-5 flex flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <BedDouble className="size-5" />
          </div>
          <div>
            <CardTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              {room.description ||
                t("room.roomDefault", {
                  number: room.roomNumber || roomIndex + 1,
                  defaultValue: `Camera ${room.roomNumber || roomIndex + 1}`,
                })}
              {room.roomNumber && (
                <span className="text-xs font-normal text-muted-foreground">
                  (N° {room.roomNumber})
                </span>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
              <Users className="size-3.5" />
              {t("room.capacity", {
                count: expectedCount,
                defaultValue: `Capienza: ${expectedCount} ${expectedCount === 1 ? "ospite" : "ospiti"}`,
              })}
            </p>
          </div>
        </div>

        <div>
          {isRoomFull ? (
            <Badge
              variant="default"
              className="bg-emerald-600 hover:bg-emerald-600 gap-1 text-xs"
            >
              <CheckCircle2 className="size-3.5" />
              {currentTotal}/{expectedCount} {t("room.roomFull", "Camera Completa")}
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1 text-xs">
              {t("room.guestsCount", {
                current: currentTotal,
                total: expectedCount,
                defaultValue: `${currentTotal}/${expectedCount} Ospiti`,
              })}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-4 sm:p-5 space-y-3">
        {/* Ospiti già registrati (dal backend) */}
        {checkedInGuests.map((cg, idx) => (
          <div
            key={`checked-${cg.id || idx}`}
            className="flex items-center justify-between p-3 rounded-xl border border-emerald-500/20 bg-emerald-50/20 dark:bg-emerald-950/15"
          >
            <div className="flex items-center gap-3">
              <div className="size-8 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <CheckCircle2 className="size-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {cg.name} {cg.surname}
                </p>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  {cg.dateOfBirth && (
                    <span>
                      {new Date(cg.dateOfBirth).toLocaleDateString()}
                    </span>
                  )}
                  {cg.nation && <span>• {cg.nation}</span>}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {cg.hasDocument && (
                <Badge
                  variant="outline"
                  className="hidden sm:inline-flex text-[11px] gap-1 border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
                >
                  <FileCheck2 className="size-3" />
                  {t("room.documentVerified", "Doc. OK")}
                </Badge>
              )}
              <Badge variant="default" className="bg-emerald-600 text-xs">
                {t("messages.completed", "Ospite completato e inviato")}
              </Badge>
            </div>
          </div>
        ))}

        {/* Ospiti in bozza aggiunti nella sessione corrente */}
        {draftGuests.map((draft, dIdx) => (
          <div
            key={`draft-${dIdx}`}
            className="flex items-center justify-between p-3 rounded-xl border border-primary/30 bg-primary/5"
          >
            <div className="flex items-center gap-3">
              <div className="size-8 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <User className="size-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {draft.name || t("room.newGuest", "Nuovo Ospite")} {draft.surname}
                </p>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  {draft.birthDate && (
                    <span>
                      {new Date(draft.birthDate).toLocaleDateString()}
                    </span>
                  )}
                  {draft.email && <span>• {draft.email}</span>}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <Badge
                variant="secondary"
                className="gap-1 text-xs border-amber-500/30 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20"
              >
                <Clock className="size-3" />
                {t("room.draftBadge", "In attesa di invio")}
              </Badge>

              <Button
                size="icon"
                variant="ghost"
                className="size-8 text-muted-foreground hover:text-foreground cursor-pointer"
                onClick={() => handleOpenEditDialog(dIdx)}
                title={t("room.edit", "Modifica")}
              >
                <Edit2 className="size-3.5" />
              </Button>

              <Button
                size="icon"
                variant="ghost"
                className="size-8 text-destructive hover:text-destructive/80 cursor-pointer"
                onClick={() => onRemoveDraftGuest(roomIndex, dIdx)}
                title={t("room.delete", "Rimuovi")}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          </div>
        ))}

        {/* Pulsante o slot per inserimento nuovo ospite */}
        {!isRoomFull ? (
          <Button
            type="button"
            variant="outline"
            className="w-full py-5 border-dashed border-2 hover:border-primary hover:bg-primary/5 text-primary gap-2 rounded-xl transition-all cursor-pointer"
            onClick={handleOpenAddDialog}
          >
            <Plus className="size-4" />
            <span className="font-semibold text-sm">
              {t("room.insertGuest", {
                current: currentTotal + 1,
                total: expectedCount,
                defaultValue: `Inserisci Dati Ospite (${currentTotal + 1} di ${expectedCount})`,
              })}
            </span>
          </Button>
        ) : checkedInGuests.length === 0 && draftGuests.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">
            {t("messages.noGuests", "Nessun ospite aggiunto per questa camera.")}
          </p>
        ) : null}
      </CardContent>

      {/* Dialog compilazione ospite */}
      <GuestDialogForm
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        guest={guestToEdit}
        roomDescription={
          room.description || `Camera ${room.roomNumber || roomIndex + 1}`
        }
        guestIndex={
          checkedInGuests.length +
          (editingGuestIndex !== null ? editingGuestIndex : draftGuests.length)
        }
        onSave={handleSaveGuest}
      />
    </Card>
  );
}
